import React, {useEffect} from 'react'
import {Outlet} from 'react-router-dom'
import styled, {keyframes} from 'styled-components'

import {identityAuth, useIdentityAuth} from '../auth'
import paths from './paths'
import {getAnalyticsUserData} from '../utils/analyticsAuth'
import {initializeAnalytics} from '../utils/analytics'

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`


const typing = keyframes`
  from { width: 0; }
  to { width: 100%; }
`

const blink = keyframes`
  0%, 50% { border-color: transparent; }
  51%, 100% { border-color: #FFD700; }
`

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`

// Styled Components
const FullScreenContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: linear-gradient(135deg, #4a2c8c 0%, #6b4e9f 100%);
  position: relative;
  overflow: hidden;
`

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  position: relative;
  background: linear-gradient(135deg, #4a2c8c 0%, #6b4e9f 100%);
`

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
`

const BackgroundPattern = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    radial-gradient(
      circle at 20% 80%,
      rgba(255, 215, 0, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 215, 0, 0.1) 0%,
      transparent 50%
    );
  opacity: 0.3;
`

const EduIntelligenceLogo = styled.div`
  font-size: 3rem;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 1rem;
  animation: ${fadeIn} 1s ease-out;
  display: flex;
  align-items: center;
  gap: 1rem;
`

const EduIcon = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #ffd700, #ffa500);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  animation: ${float} 3s ease-in-out infinite;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  animation: ${fadeIn} 1s ease-out;
`

const LoadingText = styled.div`
  font-size: 1.5rem;
  color: white;
  text-align: center;
  margin-bottom: 1rem;
`

const TypingText = styled.div`
  font-size: 1.2rem;
  color: #ffd700;
  text-align: center;
  overflow: hidden;
  white-space: nowrap;
  border-right: 2px solid #ffd700;
  animation:
    ${typing} 3s steps(40, end),
    ${blink} 0.75s step-end infinite;
  max-width: 400px;
`

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid #ffd700;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const WelbeeLogo = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  width: 56px;
  height: 56px;
  background: #ffd700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  color: #4a2c8c;
  animation: ${fadeIn} 1s ease-out 1s both;
`

const StatusMessage = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin-top: 1rem;
  animation: ${fadeIn} 1s ease-out 1.5s both;
`
const ProtectedRoute = () => {
  const skipStorage = ['/login']
  const {user, authenticated, loading} = useIdentityAuth()

  useEffect(() => {
    // Remove session parameter from URL if present (security: JWT tokens shouldn't be in URL)
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('session')) {
      urlParams.delete('session')
      const newSearch = urlParams.toString()
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : '')
      window.history.replaceState({}, '', newUrl)
    }
  }, [])

  useEffect(() => {
    console.log('user', user)
    if (user) {
      const analyticsData = getAnalyticsUserData(user)
      if (analyticsData) {
        initializeAnalytics(analyticsData)
      }
    }
  }, [user])

  useEffect(() => {
    if (loading || authenticated) return

    identityAuth.redirectToLogin()
    // A redirect held back by the cooldown must not follow the user to a page they navigate to
    // in the meantime.
    return () => identityAuth.cancelPendingRedirect()
  }, [loading, authenticated])

  // Handle redirect URL storage
  if (!skipStorage.includes(window.location.pathname)) {
    var url = window.location.pathname + window.location.search
    var path = paths.questionPanel.substring(
      0,
      paths.questionPanel.lastIndexOf('/:')
    )
    if (url.includes(path)) {
      localStorage.setItem('RedirectUrl', url)
    } else {
      localStorage.removeItem('RedirectUrl')
    }
  }

  if (loading) {
    return (
      <FullScreenContainer>
        <LeftPanel>
          <BackgroundPattern />
          <EduIntelligenceLogo>
            <EduIcon>📊</EduIcon>
            Edu Intelligence
          </EduIntelligenceLogo>
          <WelbeeLogo>W</WelbeeLogo>
        </LeftPanel>
        <RightPanel>
          <LoadingContainer>
            <LoadingText>🔐 Loading...</LoadingText>
            <TypingText>Determining your landing page...</TypingText>
            <LoadingSpinner />
            <StatusMessage>
              Please wait while we set up your dashboard
            </StatusMessage>
          </LoadingContainer>
        </RightPanel>
      </FullScreenContainer>
    )
  }

  if (!authenticated) {
    return null
  }

  return <Outlet />
}

export default ProtectedRoute
